import { LifeglovHand, LifeglovSize } from '@bioliberty/core';
import {
  Patient,
  PatientLifeglov,
  LifeglovHand as PatientLifeglovHand,
  LifeglovSize as PatientLifeglovSize
} from '@bioliberty/lifemind';
import { useCallback, useState } from 'react';
import z from 'zod';
import { useUpdatePatientMutation } from '../../../api';
import { useAddPatientLifeglovMutation } from '../../../api/lifeglovs';
import { Button } from '../../../components/Button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../../../components/Dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/Input/Select';
import { InputLabel, TextInput } from '../../../components/Input/TextInput';
import { useToaster } from '../../../hooks/useToaster';
import { useValidator } from '../../../hooks/useValidator';
import { capitalizeFirstLetter } from '../../../util';

type EditPatientModalProps = {
  oldPatient: Patient;
  oldLifeglov?: PatientLifeglov;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export const EditPatientModal = ({ oldPatient, oldLifeglov, open, onOpenChange }: EditPatientModalProps) => {
  const [alias, setAlias] = useState(oldPatient.alias);
  const [size, setSize] = useState<PatientLifeglovSize | undefined>(oldLifeglov?.size);
  const [hand, setHand] = useState<PatientLifeglovHand | undefined>(oldLifeglov?.hand);
  const toast = useToaster();

  const { validate, errors } = useValidator(
    z.object({
      alias: z
        .string()
        .regex(/\S+/, 'Please enter a valid unique patient identifier')
        .min(1, 'A unique patient identifier is required'),
      size: z.nativeEnum(LifeglovSize).optional(),
      hand: z.nativeEnum(LifeglovHand).optional()
    })
  );

  const [updatePatient, { isLoading: isPatientUpdating }] = useUpdatePatientMutation();
  const [addPatientLifeglov, { isLoading: isLifeglovUpdating }] = useAddPatientLifeglovMutation();

  const handleAliasChange = (e: string) => {
    setAlias(e);
  };

  const onEdit = useCallback(async () => {
    if (!validate({ alias, size, hand })) {
      return;
    }

    const updatedPatient = { id: oldPatient.id, alias: alias.trim() };

    try {
      await updatePatient(updatedPatient).unwrap();

      if (oldLifeglov?.size != size || oldLifeglov?.hand !== hand) {
        const newPatientLifeglov = {
          patientId: oldPatient.id,
          size: size,
          hand: hand
        };
        await addPatientLifeglov(newPatientLifeglov).unwrap();
      }
      toast.success('Patient updated successfully.');
      onOpenChange(false);
    } catch (error) {
      const errorTitle = (error as { data?: { title: string } }).data?.title || 'Error updating patient';
      toast.error(errorTitle);
    }
  }, [updatePatient, addPatientLifeglov, alias, size, hand, onOpenChange, toast, validate, oldPatient, oldLifeglov]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Patient</DialogTitle>
        </DialogHeader>

        <TextInput
          initialValue={alias}
          label="Unique patient identifier*"
          onChange={handleAliasChange}
          errorMessage={errors.alias}
          autoFocus
        />

        <div>
          <InputLabel>Lifeglov size</InputLabel>
          <Select
            defaultValue={oldLifeglov?.size}
            onValueChange={(value) => setSize(value === 'undefined' ? undefined : (value as LifeglovSize))}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select a Lifeglov size" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem key={undefined} value="undefined">
                N/A
              </SelectItem>
              {Object.values(LifeglovSize).map((size) => (
                <SelectItem key={size} value={size}>
                  {capitalizeFirstLetter(size)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <InputLabel>Lifeglov hand</InputLabel>
          <Select
            defaultValue={oldLifeglov?.hand}
            onValueChange={(value) => setHand(value === 'undefined' ? undefined : (value as LifeglovHand))}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select a Lifeglov hand" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem key={undefined} value="undefined">
                N/A
              </SelectItem>
              {Object.values(LifeglovHand).map((hand) => (
                <SelectItem key={hand} value={hand}>
                  {capitalizeFirstLetter(hand)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <DialogFooter>
          <Button
            variant={'accent'}
            onClick={onEdit}
            disabled={isPatientUpdating || isLifeglovUpdating}
            isLoading={isPatientUpdating || isLifeglovUpdating}
          >
            Update
          </Button>
          <DialogClose asChild>
            <Button variant="transparent">Cancel</Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
